import {
  trackDistributionChannelDisableCompleted,
  trackDistributionChannelDisableFailed,
  trackDistributionChannelDisableStarted,
  trackDistributionChannelEnableCompleted,
  trackDistributionChannelEnableFailed,
  trackDistributionChannelEnableStarted,
  trackSettingsDistributionChannelsViewed,
} from '@src/analytics/EventTracker';
import { useDistributionChannels } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import DistributionChannelsSectionBody from './DistributionChannelsSectionBody';

import type { DistributionChannelsState } from '@src/stores-v2/DistributionChannelsStore';
import type { DistributionChannel } from '@src/types.ts/distribution-channel';

type ViewMode = 'list' | 'comparison';
type Density = 'summary' | 'details';

const DistributionChannelsSection = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const distributionChannelsStore = useDistributionChannels();
  const showcaseId = videoCreator.organization?.id;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [density, setDensity] = useState<Density>('summary');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busyChannelId, setBusyChannelId] = useState<string | null>(null);

  useEffect(() => {
    if (!showcaseId) return;
    void distributionChannelsStore.loadForShowcase(showcaseId);
  }, [showcaseId, distributionChannelsStore]);

  const loadState: DistributionChannelsState = showcaseId
    ? distributionChannelsStore.getState(showcaseId)
    : { status: 'idle', channels: [], error: null };

  const enabledIds = useMemo(
    () => new Set(loadState.channels.map(c => c.id)),
    [loadState.channels],
  );

  const viewedFiredRef = useRef(false);
  useEffect(() => {
    if (viewedFiredRef.current) return;
    if (loadState.status !== 'ready') return;
    viewedFiredRef.current = true;
    trackSettingsDistributionChannelsViewed({
      enabled_count: loadState.channels.length,
    });
  }, [loadState.status, loadState.channels.length]);

  const handleAdd = async (channel: DistributionChannel) => {
    if (!showcaseId) return;
    setPickerOpen(false);
    setBusyChannelId(channel.id);
    const availableCountBefore =
      distributionChannelsStore.getAvailableState(showcaseId).channels.length;
    const eventProps = {
      channel_id: channel.id,
      channel_slug: channel.slug,
      channel_title: channel.title,
    };
    trackDistributionChannelEnableStarted({
      ...eventProps,
      available_count_before: availableCountBefore,
    });
    const result = await distributionChannelsStore.enableChannel(
      showcaseId,
      channel,
    );
    setBusyChannelId(null);
    if (result.ok) {
      trackDistributionChannelEnableCompleted({
        ...eventProps,
        available_count_before: availableCountBefore,
      });
    } else {
      const msg = result.error.message ?? 'Failed to add channel.';
      videoCreator.showToast({ state: 'error', message: msg });
      trackDistributionChannelEnableFailed({
        ...eventProps,
        error_message: msg,
      });
    }
  };

  const handleRemove = async (channel: DistributionChannel) => {
    if (!showcaseId) return;
    setBusyChannelId(channel.id);
    const enabledCountBefore = enabledIds.size;
    const eventProps = {
      channel_id: channel.id,
      channel_slug: channel.slug,
      channel_title: channel.title,
    };
    trackDistributionChannelDisableStarted({
      ...eventProps,
      enabled_count_before: enabledCountBefore,
    });
    const result = await distributionChannelsStore.disableChannel(
      showcaseId,
      channel.id,
    );
    setBusyChannelId(null);
    if (result.ok) {
      trackDistributionChannelDisableCompleted({
        ...eventProps,
        enabled_count_before: enabledCountBefore,
      });
    } else {
      const msg = result.error.message ?? 'Failed to remove channel.';
      videoCreator.showToast({ state: 'error', message: msg });
      trackDistributionChannelDisableFailed({
        ...eventProps,
        error_message: msg,
      });
    }
  };

  return (
    <Section>
      <header>
        <h2>Distribution Channels</h2>
        <p>
          Configure which platforms this showcase publishes to. Channels enabled
          here become available across content production.
        </p>
      </header>
      <DistributionChannelsSectionBody
        loadState={loadState}
        retry={() =>
          showcaseId &&
          void distributionChannelsStore.loadForShowcase(showcaseId, {
            force: true,
          })
        }
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        viewMode={viewMode}
        setViewMode={setViewMode}
        density={density}
        setDensity={setDensity}
        showcaseId={showcaseId}
        enabledIds={enabledIds}
        pickerOpen={pickerOpen}
        setPickerOpen={setPickerOpen}
        onAdd={handleAdd}
        onRemove={handleRemove}
        busyChannelId={busyChannelId}
      />
    </Section>
  );
});

export default DistributionChannelsSection;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  > header {
    display: flex;
    flex-direction: column;
    gap: 6px;

    > h2 {
      font-size: 20px;
      font-weight: var(--font-weight-bold);
      color: var(--foreground);
      margin: 0;
    }

    > p {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      margin: 0;
    }
  }
`;
